import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface WatchSoldier {
  watchId: string;
  soldierId: string;
  soldierName: string;
}

interface FieldEventContextValue {
  fieldEventId: string | null;
  setFieldEventId: (id: string) => void;
  boardAddress: string;
  setBoardAddress: (addr: string) => void;
  // watchId → soldier mapping, built up as pairings are confirmed
  pairings: Map<string, WatchSoldier>;
  registerPairing: (entry: WatchSoldier) => void;
  // Whether a LoRa-medic session is active (host is relaying events over LoRa)
  loraSessionActive: boolean;
  setLoraSessionActive: (active: boolean) => void;
  /** Clear all field event state (call after the event ends and user leaves). */
  resetFieldEvent: () => void;
}

const FieldEventContext = createContext<FieldEventContextValue>({
  fieldEventId: null,
  setFieldEventId: () => {},
  boardAddress: "",
  setBoardAddress: () => {},
  pairings: new Map(),
  registerPairing: () => {},
  loraSessionActive: false,
  setLoraSessionActive: () => {},
  resetFieldEvent: () => {},
});

export function FieldEventProvider({ children }: { children: ReactNode }) {
  const [fieldEventId, setFieldEventId] = useState<string | null>(null);
  const [boardAddress, setBoardAddress] = useState("");
  const [pairings, setPairings] = useState<Map<string, WatchSoldier>>(new Map());
  const [loraSessionActive, setLoraSessionActive] = useState(false);

  const registerPairing = useCallback((entry: WatchSoldier) => {
    setPairings((prev) => {
      const next = new Map(prev);
      next.set(entry.watchId, entry);
      return next;
    });
  }, []);

  const resetFieldEvent = useCallback(() => {
    setFieldEventId(null);
    setBoardAddress("");
    setPairings(new Map());
    setLoraSessionActive(false);
  }, []);

  return (
    <FieldEventContext.Provider value={{
      fieldEventId,
      setFieldEventId,
      boardAddress,
      setBoardAddress,
      pairings,
      registerPairing,
      loraSessionActive,
      setLoraSessionActive,
      resetFieldEvent,
    }}>
      {children}
    </FieldEventContext.Provider>
  );
}

export function useFieldEvent() {
  return useContext(FieldEventContext);
}
